import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useOrganization } from "@/hooks/use-organization";
import { supabase } from "@/integrations/supabase/client";

export const PORTAL_CHOICE_KEY = "vrg_portal_role";
export type PortalChoice = "operator" | "franchise";

/** Read the user's last portal choice from localStorage. */
export function getPortalChoice(): PortalChoice | null {
  return (localStorage.getItem(PORTAL_CHOICE_KEY) as PortalChoice) || null;
}

/** Persist the user's portal choice. */
export function setPortalChoice(choice: PortalChoice): void {
  localStorage.setItem(PORTAL_CHOICE_KEY, choice);
}

/**
 * Returns whether the current user belongs to both an operator org
 * (via user_roles) AND the franchise_users table.
 *
 * Use this to decide whether to show the portal switcher or the
 * /choose-portal selection screen.
 */
export function useDualRole() {
  const { user } = useAuth();
  const { allOrgs, loading: orgsLoading } = useOrganization();

  const { data: franchiseUserId = null, isLoading: franchiseLoading } = useQuery({
    queryKey: ["franchise-user-check", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("franchise_users")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();
      return (data?.id as string) ?? null;
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const isOperator = allOrgs.length > 0;
  const isFranchise = !!franchiseUserId;

  return {
    isOperator,
    isFranchise,
    isDualRole: isOperator && isFranchise,
    loading: orgsLoading || franchiseLoading,
  };
}
